/**
 * Province routing — the cross-store "brain".
 *
 * Owns: config parse (fail-closed), store identity (hostname + override), dated-rules
 * resolution, the routing decision + cross-store redirect (shared cookie + URL param +
 * loop guard), inbound choice consumption + validation, and a debug-gated logger.
 *
 * It does NOT touch the IP API and it does NOT render the popup — the popup calls
 * window.PVRouting.setProvince(code) and window.PVRouting.setAgeConfirmed().
 * Applying a resolved province to the DOM is delegated to window.PVProvinceApply
 * (defined by province-detection.js, which loads after this file) so there is a single
 * DOM-apply path shared by IP mode and popup mode.
 *
 * Loaded before province-detection.js. Gated by settings.province_filtering_enabled.
 */
(function () {
  'use strict';

  var theme = window.theme || {};

  // ---- Debug gate (opt-in only; off for real users) -----------------------------------
  function debugEnabled() {
    try {
      if (theme.provinceRouting && theme.provinceRouting.debug === true) return true;
      if (localStorage.getItem('pv-debug') === '1') return true;
    } catch (e) {}
    try {
      if (new URL(window.location.href).searchParams.get('pv_debug') === '1') return true;
    } catch (e) {}
    return false;
  }
  var DEBUG = debugEnabled();
  function log() {
    if (!DEBUG) return;
    var args = Array.prototype.slice.call(arguments);
    args.unshift('[Province Routing]');
    console.log.apply(console, args);
  }

  // ---- Config parse (fail-closed) -----------------------------------------------------
  // Liquid emits the textarea verbatim as a string. Parse once, in try/catch. On any
  // problem we fail closed: no routing, no popup province step — but the age gate still runs.
  function parseConfig() {
    var raw = theme.provinceRoutingRaw;
    if (raw == null || String(raw).trim() === '') {
      log('No routing config set — fail closed (age gate still runs, no routing).');
      return null;
    }
    var cfg;
    try {
      cfg = JSON.parse(raw);
    } catch (e) {
      log('Invalid routing JSON — fail closed.', e && e.message);
      return null;
    }
    if (!cfg || typeof cfg !== 'object' || !cfg.stores || !cfg.provinceToStore) {
      log('Routing JSON missing required keys (stores/provinceToStore) — fail closed.');
      return null;
    }
    return cfg;
  }

  var config = parseConfig();
  // Expose early so the debug logger is shared even when config is null.
  theme.provinceRouting = config || theme.provinceRouting || {};

  // ---- Store identity (hostname → override → fail closed) -----------------------------
  function resolveCurrentStore(cfg) {
    if (!cfg) return null;
    var host = '';
    try {
      host = String(window.location.hostname || '').toLowerCase();
    } catch (e) {}
    var ids = Object.keys(cfg.stores);
    for (var i = 0; i < ids.length; i++) {
      var store = cfg.stores[ids[i]];
      var domains = (store && store.domains) || [];
      for (var j = 0; j < domains.length; j++) {
        if (String(domains[j]).toLowerCase() === host) {
          log('Store identity from hostname:', ids[i], '(' + host + ')');
          return ids[i];
        }
      }
    }
    var override = theme.currentStoreOverride;
    if (override && cfg.stores[override]) {
      log('Store identity from override:', override, '(hostname', host, 'did not match)');
      return override;
    }
    log('Store identity UNRESOLVED for host', host, '— fail closed (no routing).');
    return null;
  }

  var currentStore = resolveCurrentStore(config);

  // Effective mode for THIS store. Fail-closed → null (detection treats null as "no popup,
  // no routing"; on the QC store that means the FED-only catalog just renders).
  var mode = null;
  if (config && currentStore && config.stores[currentStore]) {
    mode = config.stores[currentStore].mode || null;
  }

  // ---- Province validation ------------------------------------------------------------
  function provinceAllowlist() {
    if (config && Array.isArray(config.provinces) && config.provinces.length) {
      return config.provinces.map(function (p) { return String(p).toUpperCase(); });
    }
    // Fallback to the keys of the routing map.
    if (config && config.provinceToStore) {
      return Object.keys(config.provinceToStore).map(function (p) { return p.toUpperCase(); });
    }
    return [];
  }
  function isValidProvince(code) {
    if (!code) return false;
    return provinceAllowlist().indexOf(String(code).toUpperCase()) !== -1;
  }

  // ---- Dated rules + map → target store ------------------------------------------------
  function withinWindow(rule, today) {
    if (rule.from && today < rule.from) return false;
    if (rule.until && today > rule.until) return false;
    return true;
  }
  function resolveTargetStore(code) {
    if (!config) return null;
    var upper = String(code).toUpperCase();
    var today = String(theme.serverDate || '');
    var rules = Array.isArray(config.datedRules) ? config.datedRules : [];
    for (var i = 0; i < rules.length; i++) {
      var r = rules[i];
      if (r && String(r.province).toUpperCase() === upper && today && withinWindow(r, today)) {
        log('datedRule matched for', upper, '→', r.store, '(date', today + ')');
        return r.store;
      }
    }
    return config.provinceToStore[upper] || config.provinceToStore[code] || null;
  }

  // ---- Filter value (code → body.dataset.province for the existing filter consumers) ---
  function filterValueFor(code) {
    var fv = (config && config.filterValue) || { ON: 'ON' };
    var upper = String(code).toUpperCase();
    if (Object.prototype.hasOwnProperty.call(fv, upper)) return fv[upper];
    return fv['*'] != null ? fv['*'] : 'OTHER';
  }

  // ---- Cookies (one shared copy on the configured Domain) -------------------------------
  function cookieNames() {
    var c = (config && config.cookie) || {};
    return {
      province: c.provinceName || 'pv_province',
      age: c.ageName || 'pv_age_ok',
      // Set when the visitor picks a non-Canada country themselves (value: ISO code).
      country: c.countryName || 'pv_country_explicit',
      domain: c.domain || '',
      maxAgeDays: c.maxAgeDays != null ? Number(c.maxAgeDays) : 365,
      sameSite: c.sameSite || 'Lax'
    };
  }
  // The configured cookie domain, when this host can use it (the domain itself or a
  // subdomain). Elsewhere (*.myshopify.com, preview domains) the browser would reject it,
  // so cookies stay host-only there.
  function sharedDomain() {
    var domain = String(cookieNames().domain || '').toLowerCase().replace(/^\./, '');
    if (!domain) return '';
    var host = '';
    try {
      host = String(window.location.hostname || '').toLowerCase();
    } catch (e) {}
    return host === domain || host.slice(-(domain.length + 1)) === '.' + domain ? domain : '';
  }
  function readCookie(name) {
    var match = ('; ' + document.cookie).split('; ' + name + '=');
    if (match.length < 2) return null;
    return decodeURIComponent(match.pop().split(';').shift());
  }
  function buildCookie(name, value, domain) {
    var c = cookieNames();
    var maxAge = c.maxAgeDays * 24 * 60 * 60;
    var parts = [
      name + '=' + encodeURIComponent(value),
      'Path=/',
      'Max-Age=' + maxAge,
      'SameSite=' + c.sameSite
    ];
    if (domain) parts.push('Domain=' + domain);
    if (window.location.protocol === 'https:') parts.push('Secure');
    return parts.join('; ');
  }
  // Write ONE copy: on the shared domain when this host can use it, otherwise host-only.
  // Never both — two cookies with the same name are both sent, and browsers order them
  // differently (Safari puts the host-only copy last), so a stale per-host copy could win.
  function writeSharedCookie(name, value) {
    var domain = sharedDomain();
    if (domain) clearHostOnlyCookie(name);
    document.cookie = buildCookie(name, value, domain);
    // Read-back is DEBUG-ONLY — it cannot prove the shared-domain cookie exists
    // (document.cookie hides the Domain), so it never drives correctness.
    if (DEBUG) {
      log('cookie write', name + '=' + value, '| Domain=' + (domain || '(host-only)') + ' | read-back=', readCookie(name));
    }
  }
  function clearHostOnlyCookie(name) {
    document.cookie = name + '=; Max-Age=0; Path=/';
  }
  function clearCookie(name) {
    var domain = sharedDomain();
    clearHostOnlyCookie(name);
    if (domain) document.cookie = name + '=; Max-Age=0; Path=/; Domain=' + domain;
  }

  // Runs on every page load, before anything reads a cookie. document.cookie can't say
  // which copy is which, so: remember the value, delete the host-only copy left by older
  // versions of this script, then read again — only the shared value is left. A valid value
  // is rewritten with a full Max-Age, so Safari's 7-day cap on script-set cookies restarts
  // from the last visit.
  function settleCookies() {
    var c = cookieNames();
    var domain = sharedDomain();
    var checks = {};
    checks[c.province] = isValidProvince;
    checks[c.age] = function (v) { return v === '1'; };
    checks[c.country] = function (v) { return /^[A-Z]{2}$/.test(v) && v !== 'CA'; };
    Object.keys(checks).forEach(function (name) {
      var before = readCookie(name);
      if (domain) clearHostOnlyCookie(name);
      var value = domain ? readCookie(name) : before;
      // Only a host-only copy existed: keep its value, now as the shared cookie.
      if (value == null) value = before;
      if (value == null) return;
      if (name === c.province || name === c.country) value = String(value).toUpperCase();
      if (checks[name](value)) writeSharedCookie(name, value);
    });
  }
  if (config) settleCookies();

  // ---- Inbound choice (URL param wins; then cookie). Validated. Param is stripped. -----
  function paramName() {
    return (config && config.param) || 'pv_province';
  }
  // One-hop marker added to every cross-store redirect. A page that arrives with it never
  // redirects again on the inbound param, so two stores that disagree can't ping-pong.
  var HOP_PARAM = 'pv_hop';
  // Per-tab marker: this tab reached the store through a cross-store redirect carrying a
  // Canadian province. Cleared when the visitor explicitly picks a foreign country.
  var CA_HOP_KEY = 'pv-ca-hop';
  function markCanadianHop(code) {
    try {
      sessionStorage.setItem(CA_HOP_KEY, String(code).toUpperCase());
    } catch (e) {}
  }
  function clearCanadianHop() {
    try {
      sessionStorage.removeItem(CA_HOP_KEY);
    } catch (e) {}
  }
  function arrivedViaCanadianHop() {
    var code = null;
    try {
      code = sessionStorage.getItem(CA_HOP_KEY);
    } catch (e) {}
    return Boolean(code && isValidProvince(code));
  }

  // Shared marker: the visitor picked a non-Canada country in the selector themselves.
  // Without it, a foreign market was set automatically (geo-IP, VPN, travel) and a saved
  // Canadian province wins. Cleared when they pick Canada or a province.
  function setCountryChoice(code) {
    var upper = String(code || '').toUpperCase();
    if (/^[A-Z]{2}$/.test(upper) && upper !== 'CA') {
      writeSharedCookie(cookieNames().country, upper);
    } else {
      clearCountryChoice();
    }
  }
  function clearCountryChoice() {
    clearCookie(cookieNames().country);
  }
  function explicitForeignCountry() {
    var v = String(readCookie(cookieNames().country) || '').toUpperCase();
    return /^[A-Z]{2}$/.test(v) && v !== 'CA' ? v : null;
  }
  function consumeInbound() {
    if (!config) return null;
    var pName = paramName();
    var fromParam = null;
    var hop = false;
    try {
      var url = new URL(window.location.href);
      var raw = url.searchParams.get(pName);
      hop = url.searchParams.get(HOP_PARAM) === '1';
      if (url.searchParams.has(HOP_PARAM) && !raw) {
        url.searchParams.delete(HOP_PARAM);
        history.replaceState(null, '', url.pathname + (url.search || '') + (url.hash || ''));
      }
      if (raw) {
        var upper = String(raw).toUpperCase();
        if (isValidProvince(upper)) {
          fromParam = upper;
          log('Inbound province from param:', upper);
        } else {
          log('Inbound param province INVALID, ignored:', raw);
        }
        // Always strip the param (valid or not) so a refresh can't re-trigger.
        url.searchParams.delete(pName);
        url.searchParams.delete(HOP_PARAM);
        var clean = url.pathname + (url.search || '') + (url.hash || '');
        history.replaceState(null, '', clean);
      }
    } catch (e) {
      log('consumeInbound param error', e && e.message);
    }
    if (fromParam) {
      // Persist the validated inbound choice locally so it sticks on this store.
      writeSharedCookie(cookieNames().province, fromParam);
      // Arrived from the other store with a Canadian province: remember it for this tab, so
      // a foreign country this store still remembers (its own localization cookie, often
      // geo-IP) never sends the visitor on to the international store.
      if (hop) markCanadianHop(fromParam);
      return { code: fromParam, source: 'param', hop: hop };
    }
    var cookieVal = readCookie(cookieNames().province);
    if (cookieVal && isValidProvince(cookieVal)) {
      log('Province from cookie:', cookieVal.toUpperCase());
      return { code: cookieVal.toUpperCase(), source: 'cookie' };
    }
    return null;
  }

  // ---- Destination URL ------------------------------------------------------------------
  // Keep the visitor on the same page in the other store: products, collections, pages, blogs,
  // search and the homepage (with an optional locale prefix such as /fr). Handles are NOT
  // always shared between stores (see checkDestination). Cart/account paths fall back to the
  // other store's homepage. `variant` is dropped because variant IDs differ between stores.
  var PRESERVED_PATH = /^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?(?:(?:products|collections|pages|blogs|search)(?:\/|$)|$)/i;
  // `country` is dropped too: the hop sets the market itself, and a carried ?country= would
  // override it on arrival.
  var DROPPED_PARAMS = ['variant', 'pv_debug', 'pv_reset', 'province', 'clear', 'country'];

  function buildDestination(base, provinceCode) {
    var dest;
    try {
      dest = new URL(base);
    } catch (e) {
      return null;
    }
    var path = window.location.pathname || '/';
    if (PRESERVED_PATH.test(path)) {
      dest.pathname = path;
      var params = new URLSearchParams(window.location.search);
      DROPPED_PARAMS.concat([paramName(), HOP_PARAM]).forEach(function (k) { params.delete(k); });
      params.forEach(function (v, k) { dest.searchParams.set(k, v); });
    }
    if (provinceCode) {
      dest.searchParams.set(paramName(), provinceCode);
      dest.searchParams.set(HOP_PARAM, '1');
    }
    return dest;
  }

  // The stores' catalogs differ (store-only products and collections, a product active on one
  // store and draft on the other — Shopify Flow drafts sold-out promo products), so a hop that
  // keeps the path can land on a 404. Before the hop, ask the target store about the
  // product/collection through its public .js/.json endpoint (readable cross-origin):
  // - a product that exists with the FED/PROV tag the visitor needs keeps the path;
  // - a missing product, or one without that tag, goes to its twin (`x` ↔ `x-prov`) when the
  //   twin exists with the tag;
  // - otherwise (and for a missing collection) the target store's search for that handle,
  //   marked pv_fallback=1 (no results there → homepage, see sections/main-search.liquid).
  // A failed or slow check (network error, timeout, no fetch) keeps the path. Pages, blogs,
  // search and the homepage are not checked.
  var CHECKED_PATH = /^(\/[a-z]{2}(?:-[a-z]{2})?)?(?:\/collections\/[^/]+)?\/(products|collections)\/([^/]+)\/?$/i;
  var CHECK_TIMEOUT_MS = 2500;

  // The FED/PROV tag a product needs for a visitor with this province — the same rule as
  // checkProductPageAccess in province-filter.js. No province (non-Canada market) → FED.
  function requiredTagFor(code) {
    return code && filterValueFor(code) === 'ON' ? 'PROV' : 'FED';
  }

  // The same product under the other FED/PROV handle.
  function twinHandle(handle) {
    return /-prov$/i.test(handle) ? handle.replace(/-prov$/i, '') : handle + '-prov';
  }

  function hasTag(tags, tag) {
    return (tags || []).some(function (t) { return String(t).toUpperCase() === tag; });
  }

  function searchFallback(dest, locale, handle) {
    var terms = decodeURIComponent(handle).replace(/-(?:prov|copy(?:-\d+)?)$/i, '').replace(/-/g, ' ');
    var fallback = new URL(dest.toString());
    fallback.pathname = (locale || '') + '/search';
    fallback.searchParams.set('q', terms);
    fallback.searchParams.set('pv_fallback', '1');
    return fallback;
  }

  // cb('found', tags) | cb('missing') | cb('unknown') — any other status, network error or
  // unreadable body.
  function probeProduct(origin, handle, cb) {
    fetch(origin + '/products/' + handle + '.js', { credentials: 'omit' }).then(function (res) {
      if (res.status === 404) return cb('missing');
      if (!res.ok) return cb('unknown');
      return res.json().then(function (product) {
        cb('found', (product && product.tags) || []);
      });
    }).catch(function () {
      cb('unknown');
    });
  }

  /**
   * Calls done(next) with where to send the visitor instead of dest (see above).
   * opts.missing — dest is already known to be unusable (the page the visitor is on is a 404
   * or lacks their tag): it is not probed, and a failed check ends on the search instead.
   * Synchronous when there is nothing to check.
   */
  function checkDestination(dest, requiredTag, done, opts) {
    opts = opts || {};
    var match = dest && dest.pathname.match(CHECKED_PATH);
    if (!match) {
      done(dest);
      return;
    }
    var locale = match[1] || '';
    var kind = match[2].toLowerCase();
    var handle = match[3];
    var search = searchFallback(dest, locale, handle);
    var unchecked = opts.missing ? search : dest;
    if (typeof fetch !== 'function') {
      done(unchecked);
      return;
    }
    var settled = false;
    function finish(next, why) {
      if (settled) return;
      settled = true;
      log('checkDestination:', why, '→', next.toString());
      done(next);
    }
    setTimeout(function () { finish(unchecked, 'timeout'); }, CHECK_TIMEOUT_MS);

    if (kind === 'collections') {
      if (opts.missing) {
        finish(search, 'collection missing, sending to search');
        return;
      }
      var probe = dest.origin + '/collections/' + handle + '.json';
      fetch(probe, { credentials: 'omit' }).then(function (res) {
        if (res.status === 404) finish(search, probe + ' is 404, sending to search');
        else finish(dest, probe + ' is ' + res.status);
      }, function () {
        finish(dest, 'probe failed, keeping path');
      });
      return;
    }

    function tryTwin(why) {
      var twin = twinHandle(handle);
      probeProduct(dest.origin, twin, function (state, tags) {
        if (state === 'found' && hasTag(tags, requiredTag)) {
          var next = new URL(dest.toString());
          next.pathname = locale + '/products/' + twin;
          finish(next, why + ', twin ' + twin + ' has ' + requiredTag);
        } else {
          finish(search, why + ', twin ' + twin + ' is ' + (state === 'found' ? 'not ' + requiredTag : state) + ', sending to search');
        }
      });
    }
    if (opts.missing) {
      tryTwin(handle + ' unusable');
      return;
    }
    probeProduct(dest.origin, handle, function (state, tags) {
      if (state === 'unknown') finish(dest, 'probe failed, keeping path');
      else if (state === 'found' && hasTag(tags, requiredTag)) finish(dest, handle + ' found with ' + requiredTag);
      else tryTwin(handle + (state === 'found' ? ' lacks ' + requiredTag : ' is 404'));
    });
  }

  /**
   * A product/collection page on THIS store the visitor can't use — a 404 (e.g. an email link
   * to a product Flow drafted when it sold out) or a product without their FED/PROV tag — goes
   * to the product's twin, else to the search (no results → homepage). Returns false when
   * there is nothing to do from here (not a product/collection URL, crawler, no config).
   */
  function recoverPage(requiredTag) {
    if (!config || isCrawler()) return false;
    var here;
    try {
      here = new URL(window.location.href);
    } catch (e) {
      return false;
    }
    if (!CHECKED_PATH.test(here.pathname)) return false;
    checkDestination(here, requiredTag, function (next) {
      if (next.toString() === here.toString()) return;
      log('recoverPage:', here.pathname, '→', next.toString());
      window.location.replace(next.toString());
    }, { missing: true });
    return true;
  }

  // Crawlers are never routed between stores: each store's pages must stay crawlable as-is.
  function isCrawler() {
    return /bot|crawl|spider|slurp|mediapartners|facebookexternalhit|bingpreview|lighthouse/i.test(navigator.userAgent || '');
  }

  // Loop guard shared by province and international routing. Only a real loop trips it:
  // the same redirect (token) already happened LOOP_MAX times within LOOP_WINDOW_MS. A
  // visitor who simply comes back later (or a few times in one tab) is always routed again.
  // Timestamps live in sessionStorage; if storage fails the redirect is allowed (pv_hop
  // still stops an immediate ping-pong between the stores).
  var LOOP_KEY = 'pv-route-attempt:';
  var LOOP_WINDOW_MS = 30000;
  var LOOP_MAX = 2;
  function recentAttempts(token) {
    var now = Date.now();
    var list = [];
    try {
      list = JSON.parse(sessionStorage.getItem(LOOP_KEY + token) || '[]');
    } catch (e) {}
    // Older versions stored a plain counter: not a list, so it is simply dropped.
    if (!Array.isArray(list)) list = [];
    return list.filter(function (t) {
      return typeof t === 'number' && now - t >= 0 && now - t < LOOP_WINDOW_MS;
    });
  }
  function loopTripped(token) {
    return recentAttempts(token).length >= LOOP_MAX;
  }
  function recordAttempt(token) {
    var list = recentAttempts(token);
    list.push(Date.now());
    try {
      sessionStorage.setItem(LOOP_KEY + token, JSON.stringify(list));
    } catch (e) {}
  }
  function guardAttempt(token) {
    if (loopTripped(token)) return false;
    recordAttempt(token);
    return true;
  }

  // True when this page was opened from outside the stores: typed address, bookmark, email
  // or search link (no referrer from any configured store domain). Such a visit is a fresh
  // start, not part of a redirect loop.
  function enteredFromOutside() {
    var ref = '';
    try {
      ref = document.referrer ? String(new URL(document.referrer).hostname).toLowerCase() : '';
    } catch (e) {}
    if (!ref || !config) return true;
    var ids = Object.keys(config.stores);
    for (var i = 0; i < ids.length; i++) {
      var domains = (config.stores[ids[i]] && config.stores[ids[i]].domains) || [];
      for (var j = 0; j < domains.length; j++) {
        if (String(domains[j]).toLowerCase() === ref) return false;
      }
    }
    return true;
  }

  /**
   * What to do with a province that arrived in the URL param.
   * 'redirect' — it belongs to the other store and this is the first hop (not a crawler);
   * 'apply'    — same store, already hopped once, crawler, or no usable target.
   * @param {object} inbound  { code, hop } from consumeInbound()
   */
  function inboundAction(inbound) {
    if (!config || !currentStore || !inbound || !isValidProvince(inbound.code)) return 'apply';
    var target = resolveTargetStore(inbound.code);
    if (!target || target === currentStore || !config.stores[target]) return 'apply';
    if (inbound.hop) {
      log('inboundAction: wrong store but already hopped once — applying locally.');
      return 'apply';
    }
    if (isCrawler()) {
      log('inboundAction: crawler — not redirecting.');
      return 'apply';
    }
    return 'redirect';
  }

  // ---- Apply / route ------------------------------------------------------------------
  function applyLocally(code, source) {
    var upper = String(code).toUpperCase();
    var fv = filterValueFor(upper);
    var payload = { province: fv, country: 'CA', code: upper, source: source || 'routing', state: 'ca' };
    if (typeof window.PVProvinceApply === 'function') {
      window.PVProvinceApply(payload);
    } else {
      // Defensive fallback (province-detection.js should always be present when enabled).
      document.body.dataset.province = fv;
      document.body.dataset.provinceState = 'ca';
      document.dispatchEvent(new CustomEvent('on:province:detected', {
        detail: { province: fv, country: 'CA', isOntario: fv === 'ON' }
      }));
    }
    reflectProvinceOnDropdown(upper);
    log('Applied locally:', upper, '→ filter', fv, '(source', source + ')');
  }

  /**
   * Route or apply a chosen province.
   * @param {string} code  province code (e.g. 'ON', 'QC', 'BC')
   * @param {object} opts  { source, allowRedirect, confirmCart, explicit }
   *   explicit — the visitor picked the province themselves: skip the loop guard
   *              (a deliberate choice cannot loop and must always reach the right store).
   * @returns {object} { action: 'redirect' | 'stay' | 'cancelled' | 'rejected', target }
   */
  function routeOrApply(code, opts) {
    opts = opts || {};
    if (!config || !currentStore) {
      log('routeOrApply: fail-closed (no config/identity) — applying locally only.');
      if (isValidProvince(code)) applyLocally(code, opts.source);
      return { action: 'stay', target: currentStore };
    }
    if (!isValidProvince(code)) {
      log('routeOrApply: invalid province rejected:', code);
      return { action: 'rejected' };
    }
    var upper = String(code).toUpperCase();

    var target = resolveTargetStore(upper);
    if (!target || target === currentStore || !config.stores[target]) {
      // Same store — safe to persist + apply (no cart loss, no redirect).
      writeSharedCookie(cookieNames().province, upper);
      if (opts.explicit) clearCountryChoice();
      applyLocally(upper, opts.source);
      return { action: 'stay', target: currentStore };
    }

    // Cross-store: loop guard keyed by source→target→province. Only automatic redirects from
    // a page reached through the stores count: a visitor's own choice can't loop, and a
    // page opened from outside (typed address, bookmark, external link) is a fresh visit.
    var token = currentStore + '>' + target + '>' + upper;
    var guarded = !opts.explicit && !enteredFromOutside();
    if (guarded && loopTripped(token)) {
      log('routeOrApply: loop guard tripped for', token, '— staying put, recoverable.');
      writeSharedCookie(cookieNames().province, upper);
      applyLocally(upper, 'loop-guard-stay');
      return { action: 'stay', target: currentStore };
    }

    if (opts.confirmCart) {
      var msg = (config.messages && config.messages.cartSwitch) ||
        'Switching to our other store — your current cart will not carry over. Continue?';
      if (!window.confirm(msg)) {
        log('routeOrApply: user cancelled cross-store switch — no cookie write, state unchanged.');
        // Cancel must be a true no-op: do NOT persist the new province and do NOT apply it,
        // otherwise the rewritten cookie would route away on the next page load. Snap the
        // dropdown back to the province actually in effect.
        var keep = readCookie(cookieNames().province);
        if (keep && isValidProvince(keep)) reflectProvinceOnDropdown(keep);
        return { action: 'cancelled', target: currentStore };
      }
    }

    // Confirmed (or no confirm needed): persist the choice, then redirect.
    writeSharedCookie(cookieNames().province, upper);
    if (opts.explicit) clearCountryChoice();
    if (guarded) recordAttempt(token);

    var base = config.stores[target].url;
    checkDestination(buildDestination(base, upper), requiredTagFor(upper), function (dest) {
      if (dest && document.body) {
        // A Canadian province always means the Canada market. Each store keeps its own
        // localization cookie, so the other store may still remember a foreign country (e.g.
        // geo-IP from an earlier visit) and would send the visitor straight to the
        // international store. Posting the localization form to the other store sets Canada
        // there in the same hop; Shopify then redirects to return_to (same page, province
        // param and hop marker included).
        var returnTo = dest.pathname + (dest.search || '');
        log('routeOrApply: posting localization CA to', target, 'province', upper, '→', returnTo);
        submitLocalization(base, 'CA', returnTo);
        return;
      }
      var href = dest ? dest.toString() : (base + (base.indexOf('?') === -1 ? '?' : '&') + paramName() + '=' + upper);
      log('routeOrApply: redirecting', currentStore, '→', target, 'province', upper, '→', href);
      window.location.assign(href);
    });
    return { action: 'redirect', target: target };
  }

  // ---- International visitors -----------------------------------------------------------
  // Visitors outside Canada belong to config.internationalStore (e.g. "QC"). On any other
  // store they are sent there, to the same page. Crawlers are not redirected.
  function internationalTarget() {
    if (!config || !currentStore) return null;
    var target = config.internationalStore;
    if (!target || target === currentStore || !config.stores[target]) return null;
    return target;
  }

  // True when a non-Canada visitor on THIS store has to move to the international store.
  function needsInternationalStore(countryCode) {
    var country = String(countryCode || theme.localizationCountry || '').toUpperCase();
    return Boolean(country && country !== 'CA' && internationalTarget());
  }

  // Each store keeps its own localization cookie, so a plain redirect would land the visitor
  // on the international store in whatever country that store last had (often Canada, by
  // geo-IP), and the province cookie would bounce them straight back. Posting the standard
  // localization form to the target store sets the chosen country there in the same hop;
  // Shopify then redirects to return_to (same page).
  function submitLocalization(baseUrl, countryCode, returnTo) {
    var form = document.createElement('form');
    form.method = 'post';
    form.action = String(baseUrl || '').replace(/\/+$/, '') + '/localization';
    form.setAttribute('accept-charset', 'UTF-8');
    form.hidden = true;
    var fields = {
      form_type: 'localization',
      utf8: '\u2713',
      _method: 'put',
      country_code: String(countryCode).toUpperCase(),
      return_to: returnTo || '/'
    };
    Object.keys(fields).forEach(function (name) {
      var input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      input.value = fields[name];
      form.appendChild(input);
    });
    document.body.appendChild(form);
    form.submit();
  }

  /**
   * Send a non-Canada visitor to the international store, same page.
   * @param {object} opts  { country, explicit }
   *   country  — ISO code to set on the international store (defaults to this page's
   *              localization country). Without one, a plain redirect is used.
   *   explicit — the visitor just picked the country themselves: skip the loop guard
   *              (a deliberate click cannot loop).
   */
  function routeInternational(opts) {
    opts = opts || {};
    var target = internationalTarget();
    if (!target) return { action: 'stay' };
    if (isCrawler()) {
      log('routeInternational: crawler — not redirecting.');
      return { action: 'stay' };
    }
    if (opts.explicit) {
      // The visitor picked a foreign country themselves: the Canadian hop no longer applies,
      // and a saved province must not pull them back to Canada.
      clearCanadianHop();
      if (opts.country) setCountryChoice(opts.country);
    } else if (arrivedViaCanadianHop()) {
      // Reached this store from the other one with a Canadian province — never bounce back
      // to the international store just because this store remembers a foreign country.
      log('routeInternational: arrived via Canadian province hop — staying.');
      return { action: 'stay' };
    }
    if (!opts.explicit && !guardAttempt(currentStore + '>' + target + '>INTL')) {
      log('routeInternational: loop guard tripped — staying put.');
      return { action: 'stay' };
    }
    var initial = buildDestination(config.stores[target].url, null);
    if (!initial) return { action: 'stay' };
    var country = String(opts.country || theme.localizationCountry || '').toUpperCase();
    checkDestination(initial, requiredTagFor(null), function (dest) {
      if (country && country !== 'CA') {
        var returnTo = dest.pathname + (dest.search || '');
        log('routeInternational: posting localization', country, 'to', target, '→', returnTo);
        submitLocalization(config.stores[target].url, country, returnTo);
      } else {
        log('routeInternational: redirecting', currentStore, '→', target, '→', dest.toString());
        window.location.assign(dest.toString());
      }
    });
    return { action: 'redirect', target: target };
  }

  // Switch THIS store's market to Canada (same page). Used when a visitor arrives with a
  // Canadian province in ?pv_province but this store still remembers a foreign country —
  // otherwise the next page load would send them to the international store again.
  // Also used for a returning Canadian visitor whose market was set to a foreign country
  // automatically (see province-detection.js). Loop-guarded: if Shopify keeps the foreign
  // country anyway, stop after two attempts in a short window.
  function switchToCanada() {
    if (isCrawler()) return { action: 'stay' };
    if (!guardAttempt((currentStore || 'store') + '>CA-SWITCH')) {
      log('switchToCanada: loop guard tripped — staying put.');
      return { action: 'stay' };
    }
    // Drop ?country= so the reload doesn't set the foreign market again.
    var params = new URLSearchParams(window.location.search);
    params.delete('country');
    var query = params.toString();
    var returnTo = window.location.pathname + (query ? '?' + query : '');
    log('switchToCanada: posting localization CA →', returnTo);
    submitLocalization(window.location.origin, 'CA', returnTo);
    return { action: 'redirect', target: currentStore };
  }

  // ---- Age cookie ---------------------------------------------------------------------
  function setAgeConfirmed() {
    writeSharedCookie(cookieNames().age, '1');
    log('Age confirmed cookie written.');
  }
  function isAgeConfirmed() {
    return readCookie(cookieNames().age) === '1';
  }

  // ---- Public API ---------------------------------------------------------------------
  window.PVRouting = {
    ready: Boolean(config && currentStore),
    debug: DEBUG,
    config: config,
    mode: mode,
    currentStore: currentStore,
    log: log,
    consumeInbound: consumeInbound,
    inboundAction: inboundAction,
    resolveTargetStore: resolveTargetStore,
    filterValueFor: filterValueFor,
    isValidProvince: isValidProvince,
    provinces: provinceAllowlist,
    getProvinceCookie: function () {
      var v = readCookie(cookieNames().province);
      return v && isValidProvince(v) ? v.toUpperCase() : null;
    },
    setProvince: function (code, opts) {
      opts = opts || {};
      if (opts.allowRedirect == null) opts.allowRedirect = true;
      if (opts.allowRedirect === false) {
        if (isValidProvince(code)) { applyLocally(code, opts.source); return { action: 'stay' }; }
        return { action: 'rejected' };
      }
      return routeOrApply(code, opts);
    },
    applyLocally: applyLocally,
    routeInternational: routeInternational,
    needsInternationalStore: needsInternationalStore,
    switchToCanada: switchToCanada,
    arrivedViaCanadianHop: arrivedViaCanadianHop,
    setCountryChoice: setCountryChoice,
    explicitForeignCountry: explicitForeignCountry,
    setAgeConfirmed: setAgeConfirmed,
    isAgeConfirmed: isAgeConfirmed,
    cookieNames: cookieNames,
    recoverPage: recoverPage
  };

  // 404 page: recover once the visitor's province is applied on THIS store. A visitor who
  // belongs to the other store is hopped there instead (no event here), and that hop checks
  // its own target.
  var filtering = theme.provinceFiltering || {};
  if (config && filtering.notFound) {
    document.addEventListener('on:province:detected', function onDetected(evt) {
      document.removeEventListener('on:province:detected', onDetected);
      recoverPage(evt && evt.detail && evt.detail.isOntario ? 'PROV' : 'FED');
    });
  }

  // Reflect a chosen province on the standalone dropdown (native select value + its label).
  // Called at load (from cookie) AND after a runtime same-store choice (applyLocally) so the
  // control is not stale until the next reload.
  function reflectProvinceOnDropdown(code) {
    if (mode !== 'popup' || !code || !isValidProvince(code)) return;
    var upper = String(code).toUpperCase();
    var ddLabels = theme.provinceLabels || (config && config.provinceLabels) || {};
    document.querySelectorAll('[data-pv-province-dropdown]').forEach(function (el) {
      var select = el.querySelector('select[data-pv-province-select]');
      var option = select && select.querySelector('option[value="CA-' + upper + '"]');
      if (option) select.value = option.value;
      var label = el.querySelector('[data-loc-select-label]');
      if (label) label.textContent = ddLabels[upper] || (option && option.textContent.trim()) || upper;
    });
  }

  // In popup mode, swap the legacy ON/Other dropdown for the standalone province dropdown.
  // In ip mode (ON) the dropdown stays and the province-dd stays hidden.
  function toggleProvinceControls() {
    if (mode !== 'popup') return;
    document.querySelectorAll('[data-province-select-wrapper]').forEach(function (el) {
      el.setAttribute('hidden', '');
      el.style.display = 'none';
    });
    // The province dropdown (provinces under "Canada", styled like the country
    // selector) is the manual control in popup mode. Unhide it and reflect the
    // current choice on the button + options.
    var current = readCookie(cookieNames().province);
    if (current && !isValidProvince(current)) current = null;
    document.querySelectorAll('[data-pv-province-dropdown]').forEach(function (el) {
      el.removeAttribute('hidden');
    });
    if (current) reflectProvinceOnDropdown(current);
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', toggleProvinceControls);
  } else {
    toggleProvinceControls();
  }

  log('init', { ready: window.PVRouting.ready, currentStore: currentStore, mode: mode, debug: DEBUG, serverDate: theme.serverDate });
})();
